/**
 * MutationSagaStatus — shared, presentational read-out of a mutation saga's
 * live state for the org drawer panels. Renders the F&O / Sage side badges,
 * the raw side-error payload on failure, and (crucially) the two fallbacks:
 *
 *   - errorStatus === 403 → the caller lacks the mutation role; surface the
 *     "use HR-Ops" message + deep-link instead of a bare error.
 *   - any other thrown/terminal error → the human-readable message.
 *
 * Styling follows the drawer's shadcn tokens (Badge, dark-mode-safe colours),
 * NOT the raw Tailwind of the hr-ops PositionTransferPanel.
 */

import React from 'react';
import { AlertCircle, ExternalLink, Loader2, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type {
  MutationResponse,
  MutationSideStatus,
} from '@/services/api/mutationsService';
import type { MutationSagaPhase } from '@/hooks/useMutationSaga';

const SIDE_STATUS_STYLE: Record<MutationSideStatus, string> = {
  pending: 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300',
  in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
  succeeded: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  skipped: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
};

const SIDE_STATUS_LABEL: Record<MutationSideStatus, string> = {
  pending: 'Pending',
  in_progress: 'In progress',
  succeeded: 'Done',
  skipped: 'Skipped',
  failed: 'Failed',
};

const SideBadge: React.FC<{ label: string; status: MutationSideStatus }> = ({
  label,
  status,
}) => (
  <span className="inline-flex items-center gap-1.5 text-xs">
    <span className="text-muted-foreground">{label}</span>
    <Badge className={cn('text-[10px]', SIDE_STATUS_STYLE[status])}>
      {SIDE_STATUS_LABEL[status]}
    </Badge>
  </span>
);

export interface MutationSagaStatusProps {
  phase: MutationSagaPhase;
  mutation: MutationResponse | null;
  error: string | null;
  errorStatus: number | null;
  /** HR-Ops fallback deep-link (shown on 403). */
  hrOpsUrl?: string | null;
  onOpenHrOps?: (url: string) => void;
}

export const MutationSagaStatus: React.FC<MutationSagaStatusProps> = ({
  phase,
  mutation,
  error,
  errorStatus,
  hrOpsUrl,
  onOpenHrOps,
}) => {
  // 403 — no mutation role. Steer to HR-Ops rather than showing a raw error.
  if (errorStatus === 403) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 space-y-1.5">
          <p>You don’t have permission to make this change here — use HR-Ops.</p>
          {hrOpsUrl && onOpenHrOps && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenHrOps(hrOpsUrl)}
            >
              Open in HR-Ops
              <ExternalLink className="ml-1.5 h-3.5 w-3.5 opacity-70" />
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Human-readable failure message — shown first, prominently. A thrown
          submit (no mutation record) or a terminal-failure summary both land
          here; the raw payload lives in the collapsible below. */}
      {error && errorStatus !== 403 && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="whitespace-pre-wrap">{error}</span>
        </div>
      )}

      {mutation && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
          <div className="mb-2 flex items-center gap-2">
            {(phase === 'submitting' || phase === 'polling') && (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            )}
            <span className="text-xs text-muted-foreground">Sync status</span>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground">
              {mutation.overall_status}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <SideBadge label="F&O" status={mutation.fo_status} />
            <SideBadge label="Sage" status={mutation.sage_status} />
          </div>
          {(mutation.fo_error || mutation.sage_error) && (
            <details className="mt-2">
              <summary className="cursor-pointer select-none text-xs text-muted-foreground hover:text-foreground">
                Technical details
              </summary>
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-red-50 p-2 text-[11px] text-red-700 dark:bg-red-500/10 dark:text-red-300">
                {JSON.stringify(
                  { fo: mutation.fo_error, sage: mutation.sage_error },
                  null,
                  2,
                )}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default MutationSagaStatus;
